import React, { useState } from 'react';
import cx from 'classnames';
import { useTransition, animated } from 'react-spring';
import { caret } from '../icon';
import { useAuth0 } from '@auth0/auth0-react';
import { MENUS_TO_REMOVE } from '../../config';
interface Link {
  readonly title?: string;
  readonly link?: string;
  readonly newTab?: boolean;
  children?: Link[];
  parent?: Link;
}

interface MenuProps {
  link: Link;
  onClick: (link: Link) => void;
  isAuthenticated: boolean;
}

const Menu = ({ link, onClick, isAuthenticated }: MenuProps) => {
  const currentUrl = window.location.pathname.toLowerCase();

  // Exclude these links when user is authenticated

  return (
    <div className="p-8">
      <div className="border-t border-white border-opacity-50">
        {/* Header title */}
        {link?.title && (
          <a
            key={link?.title}
            onClick={() => onClick(link)}
            className="block relative w-full text-blue-darker sky-h4 border-b border-white border-opacity-50 py-2 font-bold hover:text-blue-light"
          >
            <div className="text-left h-8 leading-8">{link?.title}</div>
            <div
              className="w-8 h-8 absolute transform rotate-90 -translate-y-1/2"
              style={{ right: '100%', top: '50%' }}
            >
              {caret}
            </div>
          </a>
        )}

        {/* Child links */}
        {link?.children
          ?.filter(child => {
            const name = child?.title?.trim().toLowerCase() || '';
            return !(isAuthenticated && MENUS_TO_REMOVE.includes(name));
          })
          .map(childLink => (
            <a
              key={childLink?.title}
              onClick={() => onClick(childLink)}
              className={cx(
                'flex items-center justify-between w-full text-blue-darker sky-h4 border-b border-gray-dark border-opacity-50 py-2 hover:font-bold hover:text-blue-light',
                {
                  'underline font-bold font-skyBold':
                    (childLink?.link && currentUrl.includes(childLink.link.toLowerCase())) ||
                    (childLink?.title?.trim().toLowerCase() === 'discover'),
                }
              )}
            >
              <div className="text-left h-8 leading-8">{childLink?.title}</div>
              {childLink?.children && <div className="w-8 h-8 transform -rotate-90">{caret}</div>}
            </a>
          ))}
      </div>
    </div>
  );
};

let setParent = (link: Link, parent?: Link): Link => {
  let newLink = { ...link };
  if (newLink?.children) {
    newLink.children = newLink.children.map(l => setParent(l, newLink));
  }
  newLink.parent = parent;
  return newLink;
};

interface SideNavProps {
  link: Link;
  close: () => void;
}

const SideNav = ({ link, close }: SideNavProps) => {
  const { isAuthenticated } = useAuth0();
  const [currentLink, set] = useState(() => setParent(link));
  const [forward, setDirection] = useState(true);

  const click = (link: Link) => {
    if (link === currentLink && link?.parent) {
      set(link.parent);
      setDirection(false);
    } else if (link?.children) {
      set(link);
      setDirection(true);
    } else if (link?.link) {
      window.location.href = link.link;
      close();
    }
  };

  const transitions = useTransition(currentLink, item => item?.title ?? '', {
    initial: null,
    from: { opacity: forward ? 1 : 0 },
    enter: { opacity: 1 },
    leave: { opacity: forward ? 1 : 0 },
  });

  return (
    <div className="menus relative h-full overflow-hidden">
      {transitions.map(({ item, props, key }) => (
        <animated.div key={key} style={props} className="absolute inset-0 sidenavHeight">
          <Menu link={item} onClick={click} isAuthenticated={isAuthenticated} />
        </animated.div>
      ))}
    </div>
  );
};

export { SideNav };
